import WorkspacePage from '#/ui/Pages/WorkspacePage.vue';
import WorkspaceStorageCard from '#/ui/Components/Workspace/WorkspaceStorageCard.vue';
import WorkspaceProjectsCard from '#/ui/Components/Workspace/WorkspaceProjectsCard.vue';
import WorkspaceUsersCard from '#/ui/Components/Workspace/WorkspaceUsersCard.vue';

import MyPage from '#/ui/Pages/MyPage.vue';
import UserProfileCard from '#/ui/Components/User/UserProfileCard.vue';
import UserPendingInvites from '#/ui/Components/User/UserPendingInvites.vue';
import UserNotificationsCard from '#/ui/Components/User/UserNotificationsCard.vue';
import OnboardFormWizard from '#/ui/Components/Onboard/OnboardFormWizard.vue';

const NotFoundPage = () => import('#/ui/Pages/General/NotFoundPage.vue');
const HomePage = () => import('#/ui/Pages/HomePage.vue');
const JobDetailPage = () => import('#/ui/Pages/JobDetailPage.vue');
const JobSearchPage = () => import('#/ui/Pages/JobSearchPage.vue');
const ManifestSearchPage = () => import('#/ui/Pages/ManifestSearchPage.vue');
const ManifestDetailPage = () => import('#/ui/Pages/ManifestDetailPage.vue');

const MediaSearchPage = () => import('#/ui/Pages/MediaSearchPage.vue');
const FileDetailPage = () => import('#/ui/Pages/FileDetailPage.vue');
const AccountPendingInvites = () => import('#/ui/Components/AccountPendingInvites.vue');
const LivePage = () => import('#/ui/Pages/LivePage.vue');

const AppPage = () => import('#/ui/Pages/ApplicationsPage.vue'); // internal only // TODO: disable in prod or pretty up
const AppLinkPage = () => import('#/ui/Pages/AppLinkPage.vue');
const EmailVerificationPage = () => import('#/ui/Pages/EmailVerificationPage.vue');

const ManifestReviewPage = () => import('#/ui/Pages/ManifestReviewPage.vue');

const BillingPage = () => import('#/ui/Pages/BillingPage.vue');
const BillingOverviewCard = () => import('#/ui/Components/Billingv2/BillingOverviewCard.vue');
const BillingSubscriptionCard = () => import('#/ui/Components/Billingv2/BillingSubscriptionCard.vue');
const BillingPaymentsCard = () => import('#/ui/Components/Billingv2/BillingPaymentsCard.vue');
const BillingHistoryCard = () => import('#/ui/Components/Billingv2/BillingHistoryCard.vue');

const appRoutes = [
  {
    path: '/apps',
    name: 'Desktop Apps',
    component: AppPage,
  },
];

const linkRoutes = [
  // handles redirecting from desktop apps to the most appropriate page based on query params
  {
    path: '/applink',
    name: 'Redirecting...',
    component: AppLinkPage,
    props: (route) => ({
      // TODO: appId - version?
      // appId: route.query.app, // the calling desktop app id.
      to: route.query.to, // where are we ultimately rerouting to
      user: route.query.user, // calling user from desktop app. allows us to know if a different user is signed in.
      workspace: route.query.workspace, // workspace scope to route to
      project: route.query.project, // project scope to route to
      job: route.query.job, // the job to route to
      entitySize: route.query.esize, // the entity size for entity id computation.
      entityHash: route.query.ehash, // the entity hash for entity id computation.
      resourceSize: route.query.rsize, // the resource size for resource id computation.
      resourceHash: route.query.rhash, // the resource hash for resource id computation.
    }),
  },
];

const workspaceRoutes = [
  {
    path: '/workspace/:workspaceId',
    name: 'Workspace',
    component: WorkspacePage,
    redirect: (to) => {
      // the function receives the target route as the argument
      // return redirect path/location here.
      console.info('WORKSPACE REDIRECT', to);
      const { workspaceId } = to.params;
      return `/workspace/${workspaceId}/projects`;
    },
    children: [
      // Note we provide the above parent route name on the default child tab
      // route to ensure this tab is rendered by default when using named routes
      { path: 'projects', component: WorkspaceProjectsCard, name: 'Projects' },
      { path: 'users', component: WorkspaceUsersCard, name: 'Users' },
      { path: 'storage', component: WorkspaceStorageCard, name: 'Storage' },
      { path: 'invites', component: AccountPendingInvites, name: 'Invites' },
    ],
  },
  {
    path: '/billing',
    name: 'Billing',
    component: BillingPage,
    children: [
      // Note we provide the above parent route name on the default child tab
      // route to ensure this tab is rendered by default when using named routes
      { path: '', component: BillingOverviewCard, name: 'Overview' },
      { path: 'subscription', component: BillingSubscriptionCard, name: 'Subscription' },
      { path: 'payments', component: BillingPaymentsCard, name: 'Payment' },
      { path: 'history', component: BillingHistoryCard, name: 'History' },
    ],
  },
].map((route) => {
  // adds the requiresProject flag to each project route
  const r = route;
  if (!r.meta) {
    r.meta = {};
  }
  r.meta.requiresWorkspace = true; // all of these routes require a workspace id in the url params
  return r;
});

const projectRoutes = [
  {
    path: '/home',
    name: 'Home',
    component: HomePage,
    // meta: { requiresProject: true },
  },
  {
    path: '/manifest-review',
    name: 'Manifest Review',
    component: ManifestReviewPage,
  },
  {
    path: '/live',
    name: 'Live',
    component: LivePage,
    props: (route) => ({ viewBy: route.query.viewBy }),
  },
  {
    path: '/manifests',
    name: 'Manifest Search',
    component: ManifestSearchPage,
  },
  {
    path: '/manifests/:manifestId',
    name: 'Manifest Details',
    component: ManifestDetailPage,
  },
  {
    path: '/jobs',
    name: 'Job Search',
    component: JobSearchPage,
  },
  {
    path: '/jobs/:jobId',
    name: 'Job Details',
    component: JobDetailPage,
  },
  {
    path: '/media',
    name: 'Media Search',
    component: MediaSearchPage,
  },
  {
    path: '/files/:size/:checksum',
    name: 'File Details',
    component: FileDetailPage,
  },
].map((route) => {
  // adds the requiresProject flag to each project route
  const r = route;
  if (!r.meta) {
    r.meta = {};
  }
  r.meta.requiresProject = true;
  return r;
});

const myRoutes = [
  {
    path: '/onboard',
    name: 'Onboard',
    component: OnboardFormWizard,
  },
  {
    path: '/my',
    name: 'My',
    component: MyPage,
    redirect: '/my/profile',
    children: [
      { path: 'profile', component: UserProfileCard, name: 'Profile' },
      { path: 'invites', component: UserPendingInvites, name: 'Invites' },
      { path: 'notifications', component: UserNotificationsCard, name: 'Notifications' },
    ],
  },
  {
    path: '/email-verify',
    name: 'Email Verification',
    component: EmailVerificationPage,
  },
  {
    // email invites and some old pages link to /invites rather than /my/invites
    path: '/invites',
    redirect: '/my/invites',
  },
];

const routes = [
  {
    path: '/',
    name: 'Index',
    redirect: '/home', // redirect to project home.
  },

  /* TODO: Remove this page/move it to admin panel */
  ...appRoutes,

  ...workspaceRoutes,
  ...projectRoutes,
  ...linkRoutes,
  ...myRoutes,
  { path: '*', component: NotFoundPage },
];

export default routes;
